import useUser from 'core/hooks/useUser';
import {ForloebKladdeModel} from "../../sharedmodels/stilling/forloebKladdeModel";
import {ValidationForm} from "../../components/validation/components/validationForm";
import {
    BuildExistingFromKladde,
    CrudForloebInputModel,
    crudForloebValidationSchema,
    crudForloebWarningSchema
} from "../forloeb/crudForloebInputModel";
import {setSlideinState, SlideInStateEnum} from "../../components/slideIn/slideInSlice";
import {HandleError} from "../../infrastructure/errors/errorBoundary";
import {ForloebCrudFields} from "../forloeb/forloebCrudFields";
import {ValidationTextarea} from "../../components/validation/components/validationTextarea";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {nameof} from "ts-simple-nameof";
import {ValidationFormSubmitButton} from "../../components/validation/components/validationFormButton";
import {useAppDispatch} from "../../../app/hooks";
import {NotificationModule} from "ditmer-embla";
import useLogbogApi from "../../hooks/useLogbogApi";
import {pingRefreshFilter} from "../../components/forloebOverview/forloebOverviewFiltersSlice";

type EditForloebKladdeProps = {
    modalId: string,
    model: ForloebKladdeModel,
}

export function CreateForloebFromKladde({model, modalId}: EditForloebKladdeProps) {
    const {currentUser} = useUser();
    const dispatch = useAppDispatch();

    const {forloebApi} = useLogbogApi();

    const createFromKladde = async (values: CrudForloebInputModel) => {
        dispatch(setSlideinState({state: SlideInStateEnum.ActionOngoing, slideInId: modalId}));

        const newForloeb = await forloebApi.createForloebAndDeleteKladde(values, model.forloebKladdeId);

        if (newForloeb) {
            dispatch(setSlideinState({state: SlideInStateEnum.Closing, slideInId: modalId}));
            NotificationModule.showSuccess(Localizer.forloebOverviewPage_createdForloeb(), "");
            dispatch(pingRefreshFilter());
        }
    };

    const editForloebKladde = async (values: CrudForloebInputModel) => {
        dispatch(setSlideinState({state: SlideInStateEnum.ActionOngoing, slideInId: modalId}));

        const forloebKladde = await forloebApi.editForloebKladde(values, model.forloebKladdeId);

        if (forloebKladde) {
            dispatch(setSlideinState({state: SlideInStateEnum.Closing, slideInId: modalId}));
            NotificationModule.showSuccess(Localizer.forloebOverviewPage_edittedForloebKladde(), "");
            dispatch(pingRefreshFilter());
        }
    }

    const deleteForloebKladde = async () => {
        dispatch(setSlideinState({state: SlideInStateEnum.ActionOngoing, slideInId: modalId}));

        const forloebKladde = await forloebApi.deleteForloebKladde(model.forloebKladdeId);

        if (forloebKladde) {
            dispatch(setSlideinState({state: SlideInStateEnum.Closing, slideInId: modalId}));
            NotificationModule.showSuccess(Localizer.forloebOverviewPage_deletedForloebKladde(), "");
            dispatch(pingRefreshFilter());
        }
    }

    return <>
        <ValidationForm
            initialValues={BuildExistingFromKladde(model)}
            validationSchema={crudForloebValidationSchema}
            warningSchema={crudForloebWarningSchema}
            onSubmit={async (values) => {
                await createFromKladde(values)
                    .catch(err => {
                        dispatch(setSlideinState({state: SlideInStateEnum.Opened, slideInId: modalId}))
                        HandleError(err);
                    });
            }}>
            {
                (formik) => {
                    return (
                        <>
                            <ForloebCrudFields modalId={modalId}
                                               formik={formik}
                                               forloebFraVU={model.kommerFraVu}
                                               isCreateFromKladde={true}
                                               isKladde
                            />

                            {(currentUser.IsAdminOrVus() || currentUser.IsHrMedarbejder()) &&
                                <div className="row">
                                    <div className="col-12">
                                        <ValidationTextarea
                                            model={{
                                                label: Localizer.forloebpage_BemaerkningerTilForloeb(),
                                                placeholder: Localizer.forloebpage_BemaerkningerTilForloebLabel(),
                                                htmlName: nameof<CrudForloebInputModel>(x => x.infoTekst)
                                            }}
                                            inputId={nameof<CrudForloebInputModel>(x => x.infoTekst)}
                                            formikProps={formik}
                                            readOnly={model.kommerFraVu}
                                        />
                                    </div>
                                </div>
                            }

                            <div className="modal-actions">
                                <button className={"btn btn-danger margin-right-s"}
                                        type={"button"}
                                        disabled={!formik.values.forloebKladde}
                                        onClick={() => deleteForloebKladde()}>
                                    {Localizer.global_delete()}
                                </button>
                                <button className={"btn btn-default margin-right-s"}
                                        disabled={!formik.values.forloebKladde}
                                        onClick={() => editForloebKladde(formik.values)}
                                        type={"submit"}>
                                    {Localizer.global_save()}
                                </button>
                                <ValidationFormSubmitButton formikProps={formik} disabled={formik.values.forloebKladde}>
                                    {Localizer.forloebpage_createForloeb()}
                                </ValidationFormSubmitButton>
                            </div>
                        </>
                    );
                }
            }
        </ValidationForm>
    </>
}
